@defer (when wasDataLoadedOnce$ | async) {
  <div class="flex flex-col flex-1 min-h-0 m-4 mt-8">
    <div class="mx-4">
      <span class="flex items-center gap-1.5 relative">
        <div class="text-navy-900 font-semibold text-lg">
          {{ PCO.title }}
        </div>
        <app-styled-options-list
          [listItems]="editOptions"
          (selectedOption)="registerOption($event)"
          [stopClickPropagation]="true"
        >
          <span class="icon-options-inverted cursor-pointer text-blue-700 flex items-center"></span>
        </app-styled-options-list>
        <div class="ml-auto gap-2 flex align-center absolute right-0 mb-0 mt-5 mr-3 items-center">
          @if (isAnyItemEdited$ | async) {
            <span class="text-navy-900 font-semibold text-base capitalize cursor-default">
              Items are being edited
            </span>
            <span
              appTooltip="Click on the check mark on all currently edited items to save"
              tooltipPosition="bottom"
              class="text-yellow-500 icon-warning"
            ></span>
          } @else if ((autoSave$ | async) === 'syncing') {
            <span class="text-navy-900 font-semibold text-base capitalize">Syncing</span>
            <div class="dots-container">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          } @else if ((autoSave$ | async) === 'synced') {
            <span class="text-navy-900 font-semibold text-base capitalize cursor-default">{{ autoSave$ | async }}</span>
            <span class="text-green-600 icon-checkbox-round"></span>
          } @else if ((autoSave$ | async) === 'error') {
            <span class="text-navy-900 font-semibold text-base capitalize cursor-default"> Could not sync </span>
            <span class="text-red-500 icon-warning"></span>
          } @else {
            <div appTooltip="All line items must have a selected value.">
              <span class="text-navy-900 font-semibold text-base capitalize cursor-default">
                {{ autoSave$ | async }}
              </span>
              <span class="text-yellow-500 icon-warning"></span>
            </div>
          }
        </div>
      </span>
      <div class="text-navy-900 font-normal text-sm">{{ PCO.number }}</div>
    </div>

    <div class="mt-6 border-b border-b-solid border-navy-300"></div>

    <div class="my-2 flex justify-between gap-2 items-center m-4 min-h-10">
      <div class="flex-1 max-w-[630px] text-ellipsis line-clamp-2 items-center">
        {{ PCO?.description ?? 'Missing description.' }}
      </div>
      <div class="flex gap-2 items-center">
        @if (PCO?.files?.length > 0) {
          <app-attachments-dropdown
            [zipFileName]="PCO.number + '_attachments'"
            [files]="PCO.files"
            fileCardClass="h-[24px]"
            iconSize="text-size_xl"
          ></app-attachments-dropdown>
        }
        <div class="flex items-center justify-center">
          <app-status-block class="w-full" [status]="PCO.status"></app-status-block>
        </div>
      </div>
    </div>

    <div class="border-b border-b-solid border-navy-300"></div>

    <app-dynamic-projected-table
      class="block"
      [isEmpty]="false"
      [wasDataLoadedOnce]="wasDataLoadedOnce$ | async"
      [isLoading]="isLoading$ | async"
    >
      <div tableHeader class="grid-table">
        <div class="grid-row table-header header-row text-xs text-navy-900 font-semibold bg-navy-100">
          <div class="item">Line Item & Description</div>
          <div></div>
          <div class="item flex flex-col">
            <span>Estimated<br />Dollars</span>
          </div>
          <div></div>
          <div class="item">Confirmed<br />Dollars</div>
          <div></div>
          <div class="item pl-5">Commitment Reference</div>
        </div>
      </div>

      <div projectedTable class="grid-table">
        @for (pcoItem of PCO.pco_items; track index; let index = $index) {
          <app-expansion-panel
            [expanded]="expandedItems.get(index)"
            (expandedChange)="expandedItems.set(index, $event)"
          >
            <app-pco-details-header
              #pcoDetailHeader
              header-row
              [isExpanded]="expandedItems.get(index)"
              (deleteItem)="deleteUnmarkedLineItem(pcoItem.id)"
              (addPCORow)="addItem($event, pcoItem)"
              [data]="pcoItem"
              [pco]="PCO"
              (valueChanged)="updateUnmarkedLineItem($event, index)"
            />

            <div content-row>
              @for (cost of pcoItem.pco_item_costs; let costIndex = $index; track costIndex) {
                <app-pco-details-expansion-content
                  [lineItemId]="pcoItem.item_id"
                  (deleteItem)="deleteCost(pcoItem, costIndex)"
                  [data]="cost"
                  (valueChanged)="updatePCOCost($event, pcoItem, costIndex)"
                ></app-pco-details-expansion-content>
              } @empty {
                <span
                  class="border-b border-solid border-b-navy-300 flex flex-col items-center font-semibold text-navy-500 p-4"
                  >There are no costs.
                  <span
                    (click)="addItem(OPTIONS.ADD_SPEND_DESCRIPTION, pcoItem)"
                    class="text-blue-700 font-bold cursor-pointer"
                    >Click here to add cost.</span
                  ></span
                >
              }
            </div>
          </app-expansion-panel>
        } @empty {
          <span
            class="border-b border-solid border-b-navy-300 flex flex-col items-center font-semibold text-navy-500 p-4"
            >There are no unmarked line item.
            <span (click)="addBudgetLine()" class="text-blue-700 font-bold cursor-pointer"
              >Click here to add.</span
            ></span
          >
        }

        <app-expansion-panel #markupLineItemPanel>
          <app-pco-detail-markups-header
            [isExpanded]="markupLineItemPanel.showContent"
            (markupToggled)="onMarkupToggled()"
            header-row
          ></app-pco-detail-markups-header>
          <div content-row>
            @for (pcoItem of markupLineItem$ | async; track pcoItem.id + '_' + index; let index = $index) {
              <app-pco-detail-markup-line-item
                [data]="pcoItem"
                (valueChange)="updateMarkupLineItem($event, index)"
              ></app-pco-detail-markup-line-item>
            }
          </div>
        </app-expansion-panel>
      </div>

      <div tableFooter class="grid-table totals">
        <div class="grid-row table-footer">
          <div class="item">{{ PCO.number }} Total</div>
          <span class="vertical-separator"></span>
          <div [class.negative]="(combinedBudgetTotals$ | async) < 0" class="item flex justify-center gap-1">
            {{ combinedBudgetTotals$ | async | money: 'positiveComa' }}
          </div>
          <span class="vertical-separator"></span>
          <div [class.negative]="(combinedCommitmentTotals$ | async) < 0" class="item flex justify-center gap-1">
            {{ combinedCommitmentTotals$ | async | money: 'positiveComa' }}
          </div>
          <span class="vertical-separator"></span>
          <div class="item"></div>
        </div>
      </div>
    </app-dynamic-projected-table>
  </div>
} @placeholder {
  <app-page-loading></app-page-loading>
}
